import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { X, ArrowRight, ArrowLeft, Footprints, Award, RefreshCw, FileText, BookOpen, Sparkles, CheckCircle2, Clock, Shield, KeyRound } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { LucideIcon } from "lucide-react";

interface TourStep {
  title: string;
  description: string;
  icon: LucideIcon;
  route?: string;
  preview?: () => JSX.Element;
}

/* ── Mini preview components ── */

function PreviewWelcome() {
  return (
    <div className="bg-muted/50 rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Shield className="h-5 w-5 text-primary" />
        <span className="font-display font-bold text-sm">CredentialEdge</span>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {["Dashboard", "Credentials", "Renewals"].map((l) => (
          <div key={l} className="bg-background rounded px-2 py-1.5 text-[10px] text-center font-medium text-muted-foreground border">{l}</div>
        ))}
      </div>
      <p className="text-[10px] text-muted-foreground text-center">Your credentialing command center</p>
    </div>
  );
}

function PreviewDIY() {
  const items = [
    { label: "Gather documents", done: true },
    { label: "Register NPI", done: true },
    { label: "CMS I&A account", done: false },
    { label: "Medicare enrollment", done: false },
  ];
  return (
    <div className="bg-muted/50 rounded-lg p-3 space-y-1.5">
      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Step-by-step guide</div>
      {items.map((it) => (
        <div key={it.label} className="flex items-center gap-2 bg-background rounded px-2 py-1 border">
          <CheckCircle2 className={`h-3.5 w-3.5 ${it.done ? "text-primary" : "text-muted-foreground/30"}`} />
          <span className={`text-xs ${it.done ? "line-through text-muted-foreground" : ""}`}>{it.label}</span>
        </div>
      ))}
    </div>
  );
}

function PreviewCredentials() {
  const rows = [
    { name: "LPC License", status: "Active", color: "text-green-600" },
    { name: "NPI Number", status: "Active", color: "text-green-600" },
    { name: "CAQH Profile", status: "Pending", color: "text-amber-600" },
  ];
  return (
    <div className="bg-muted/50 rounded-lg p-3 space-y-1.5">
      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Your credentials</div>
      {rows.map((r) => (
        <div key={r.name} className="flex items-center justify-between bg-background rounded px-2 py-1 border">
          <span className="text-xs">{r.name}</span>
          <span className={`text-[10px] font-medium ${r.color}`}>{r.status}</span>
        </div>
      ))}
    </div>
  );
}

function PreviewRenewals() {
  const items = [
    { name: "LPC License", days: 45, pct: 88 },
    { name: "DEA Cert", days: 120, pct: 67 },
    { name: "Malpractice", days: 210, pct: 42 },
  ];
  return (
    <div className="bg-muted/50 rounded-lg p-3 space-y-1.5">
      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Renewal timeline</div>
      {items.map((it) => (
        <div key={it.name} className="bg-background rounded px-2 py-1.5 border space-y-1">
          <div className="flex justify-between text-xs">
            <span>{it.name}</span>
            <span className="text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{it.days}d</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full" style={{ width: `${it.pct}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function PreviewDocuments() {
  const docs = ["LPC_License_2024.pdf", "NPI_Confirmation.pdf", "W9_Form.pdf"];
  return (
    <div className="bg-muted/50 rounded-lg p-3 space-y-1.5">
      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Document library</div>
      {docs.map((d) => (
        <div key={d} className="flex items-center gap-2 bg-background rounded px-2 py-1 border">
          <FileText className="h-3.5 w-3.5 text-primary" />
          <span className="text-xs truncate">{d}</span>
        </div>
      ))}
    </div>
  );
}

function PreviewResources() {
  const cats = [
    { label: "Federal Forms", count: 5 },
    { label: "State Links", count: 8 },
    { label: "MCO Portals", count: 4 },
  ];
  return (
    <div className="bg-muted/50 rounded-lg p-3 space-y-1.5">
      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Resource library</div>
      <div className="grid grid-cols-3 gap-1.5">
        {cats.map((c) => (
          <div key={c.label} className="bg-background rounded p-2 border text-center">
            <BookOpen className="h-3.5 w-3.5 text-primary mx-auto mb-1" />
            <div className="text-[10px] font-medium">{c.label}</div>
            <div className="text-[10px] text-muted-foreground">{c.count} links</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PreviewFinish() {
  return (
    <div className="bg-muted/50 rounded-lg p-3 space-y-2 text-center">
      <div className="flex items-center justify-center gap-1">
        <Sparkles className="h-5 w-5 text-primary" />
        <KeyRound className="h-5 w-5 text-primary" />
      </div>
      <p className="text-xs font-medium">Your dashboard is ready!</p>
      <div className="flex justify-center gap-2">
        {["DIY Guide", "Help & FAQ"].map((l) => (
          <div key={l} className="bg-primary/10 text-primary text-[10px] font-medium rounded-full px-3 py-1">{l}</div>
        ))}
      </div>
    </div>
  );
}

/* ── Tour steps ── */

const TOUR_STEPS: TourStep[] = [
  {
    title: "Welcome to CredentialEdge!",
    description: "Let's take a quick tour of the key features that will help you manage your credentialing journey. This will only take a minute.",
    icon: Sparkles,
    preview: PreviewWelcome,
  },
  {
    title: "Get Credentialed (DIY Guide)",
    description: "Follow our step-by-step guide to complete your credentialing process. Track your progress with checklists, save important IDs, and upload required documents.",
    icon: Footprints,
    route: "/diy",
    preview: PreviewDIY,
  },
  {
    title: "Credentials & Renewal Hub",
    description: "View and manage all your credentials in one place. Track statuses, renewal deadlines, and credential numbers. Sync your calendar and never miss a deadline.",
    icon: Award,
    route: "/my-credentials",
    preview: PreviewCredentials,
  },
  {
    title: "Documents",
    description: "Upload and organize all your credentialing documents. Use our naming conventions to keep files consistent and easy to find.",
    icon: FileText,
    route: "/documents",
    preview: PreviewDocuments,
  },
  {
    title: "Resources",
    description: "Access a curated library of forms, tools, and links organized by category — everything you need in one place.",
    icon: BookOpen,
    route: "/resources",
    preview: PreviewResources,
  },
  {
    title: "You're all set!",
    description: "Start by heading to the DIY Guide to begin your credentialing process. You can always revisit this tour from Settings or the Help page.",
    icon: Sparkles,
    route: "/dashboard",
    preview: PreviewFinish,
  },
];

const STORAGE_KEY = "credentialedge-tour-completed";

const tourEvent = new EventTarget();
const TOUR_START_EVENT = "tour-start";

export function useOnboardingTour() {
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(STORAGE_KEY);
    if (!completed) {
      const timer = setTimeout(() => setShowTour(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const handler = () => setShowTour(true);
    tourEvent.addEventListener(TOUR_START_EVENT, handler);
    return () => tourEvent.removeEventListener(TOUR_START_EVENT, handler);
  }, []);

  const startTour = () => tourEvent.dispatchEvent(new Event(TOUR_START_EVENT));
  const completeTour = () => {
    setShowTour(false);
    localStorage.setItem(STORAGE_KEY, "true");
  };

  return { showTour, startTour, completeTour };
}

interface Props {
  onComplete: () => void;
}

export default function OnboardingTour({ onComplete }: Props) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const current = TOUR_STEPS[step];
  const Icon = current.icon;
  const Preview = current.preview;
  const isLast = step === TOUR_STEPS.length - 1;

  const goNext = () => {
    if (isLast) {
      onComplete();
      if (TOUR_STEPS[step].route) navigate(TOUR_STEPS[step].route!);
      return;
    }
    const next = TOUR_STEPS[step + 1];
    if (next.route && location.pathname !== next.route) {
      navigate(next.route);
    }
    setStep(step + 1);
  };

  const goBack = () => {
    if (step === 0) return;
    const prev = TOUR_STEPS[step - 1];
    if (prev.route && location.pathname !== prev.route) {
      navigate(prev.route);
    }
    setStep(step - 1);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-300">
      <Card className="w-full max-w-lg mx-4 mb-4 sm:mb-0 shadow-xl border-primary/20">
        <CardContent className="pt-6 pb-4 space-y-4">
          {/* Close button */}
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Icon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-display font-bold text-lg leading-tight">{current.title}</h3>
                <p className="text-xs text-muted-foreground">
                  Step {step + 1} of {TOUR_STEPS.length}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onComplete} className="h-8 w-8 -mt-1 -mr-2">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Visual preview */}
          {Preview && (
            <div key={step} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
              <Preview />
            </div>
          )}

          <p className="text-sm text-muted-foreground leading-relaxed">
            {current.description}
          </p>

          {/* Progress dots */}
          <div className="flex justify-center gap-1.5">
            {TOUR_STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === step ? "w-6 bg-primary" : i < step ? "w-1.5 bg-primary/40" : "w-1.5 bg-muted"
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center pt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={goBack}
              disabled={step === 0}
              className="gap-1"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </Button>
            <Button size="sm" onClick={goNext} className="gap-1">
              {isLast ? "Get Started" : "Next"} <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
